import * as maptilersdk from '@maptiler/sdk';
import { logger } from './logger';

/**
 * PopupManager - Centralized popup state management for the map
 *
 * Responsibilities:
 * - Track all open popups
 * - Ensure only one popup is open at a time
 * - Clean up popup references when markers are recreated
 * - Provide type-safe popup operations
 */
export class PopupManager {
  private openPopups: Set<maptilersdk.Popup>;
  private openPopupMarkerId: number | string | null;

  constructor() {
    this.openPopups = new Set();
    this.openPopupMarkerId = null;
  }

  /**
   * Open a popup, automatically closing any others
   * @param popup - The popup to open
   * @param map - The map instance to add the popup to
   * @param markerId - The vendor ID associated with this popup
   * @param marker - The marker this popup is attached to
   */
  open(
    popup: maptilersdk.Popup,
    map: maptilersdk.Map,
    markerId: number | string,
    marker: maptilersdk.Marker
  ): void {
    logger.debug('PopupManager: Opening popup for marker', markerId);

    // Close any existing popups first
    this.closeAll();

    // Open the new popup
    try {
      popup.setLngLat(marker.getLngLat());
      popup.addTo(map);

      // Track it
      this.openPopups.add(popup);
      this.openPopupMarkerId = markerId;

      logger.debug('PopupManager: Popup opened and tracked', {
        markerId,
        totalTracked: this.openPopups.size,
      });
    } catch (error) {
      logger.error('PopupManager: Error opening popup', error);
      throw error;
    }
  }

  /**
   * Close a specific popup
   * @param popup - The popup to close
   */
  close(popup: maptilersdk.Popup): void {
    if (this.openPopups.has(popup)) {
      try {
        popup.remove();
        this.openPopups.delete(popup);

        logger.debug('PopupManager: Closed specific popup', {
          remainingPopups: this.openPopups.size,
        });
      } catch (error) {
        logger.warn('PopupManager: Error closing popup', error);
      }
    }
  }

  /**
   * Close all open popups
   */
  closeAll(): void {
    const count = this.openPopups.size;

    if (count === 0) {
      return;
    }

    logger.debug('PopupManager: Closing all popups', { count });

    this.openPopups.forEach(popup => {
      try {
        popup.remove();
      } catch (error) {
        logger.warn('PopupManager: Error removing popup during closeAll', error);
      }
    });

    this.openPopups.clear();
    this.openPopupMarkerId = null;

    logger.debug('PopupManager: All popups closed');
  }

  /**
   * Close all popups except the one attached to a specific marker
   * @param keepMarker - The marker whose popup should NOT be closed
   * @param markers - Array of all markers to check
   */
  closeAllExcept(keepMarker: maptilersdk.Marker, markers: maptilersdk.Marker[]): void {
    logger.debug('PopupManager: Closing all popups except one');

    let closedCount = 0;

    markers.forEach(marker => {
      if (marker !== keepMarker) {
        const popup = marker.getPopup();
        if (popup && this.openPopups.has(popup)) {
          try {
            popup.remove();
            this.openPopups.delete(popup);
            closedCount++;
          } catch (error) {
            logger.warn('PopupManager: Error closing popup', error);
          }
        }
      }
    });

    if (closedCount > 0) {
      this.openPopupMarkerId = null;
    }

    logger.debug('PopupManager: Closed popups', {
      closedCount,
      remainingTracked: this.openPopups.size,
    });
  }

  /**
   * Check if a popup is currently tracked as open
   * @param popup - The popup to check
   * @returns true if the popup is in the tracking set
   */
  isTracked(popup: maptilersdk.Popup): boolean {
    return this.openPopups.has(popup);
  }

  /**
   * Get the ID of the marker with the currently open popup
   * @returns The marker ID, or null if no popup is open
   */
  getOpenMarkerId(): number | string | null {
    return this.openPopupMarkerId;
  }

  /**
   * Get the count of tracked popups
   * @returns Number of popups currently tracked
   */
  getTrackedCount(): number {
    return this.openPopups.size;
  }

  /**
   * Clean up all popup tracking when markers are recreated
   * This should be called BEFORE markers are removed/recreated
   * @param markers - Array of markers whose popups should be cleaned up
   */
  cleanupBeforeMarkerRecreation(markers: maptilersdk.Marker[]): void {
    logger.debug('PopupManager: Cleaning up before marker recreation', {
      markerCount: markers.length,
      trackedPopups: this.openPopups.size,
    });

    markers.forEach(marker => {
      const popup = marker.getPopup();
      if (popup) {
        try {
          popup.remove();
          this.openPopups.delete(popup);
        } catch (error) {
          logger.warn('PopupManager: Error during cleanup', error);
        }
      }
    });

    this.openPopups.clear();
    this.openPopupMarkerId = null;

    logger.debug('PopupManager: Cleanup complete');
  }

  /**
   * Register a popup with the manager (for popups opened via marker clicks)
   * This is called when the map SDK opens a popup via marker.togglePopup()
   * @param popup - The popup that was opened
   * @param markerId - The vendor ID associated with this popup
   */
  register(popup: maptilersdk.Popup, markerId: number | string): void {
    this.openPopups.add(popup);
    this.openPopupMarkerId = markerId;

    logger.debug('PopupManager: Registered popup', {
      markerId,
      totalTracked: this.openPopups.size,
    });
  }

  /**
   * Unregister a popup from the manager (for popups closed via marker clicks)
   * @param popup - The popup that was closed
   */
  unregister(popup: maptilersdk.Popup): void {
    this.openPopups.delete(popup);

    if (this.openPopups.size === 0) {
      this.openPopupMarkerId = null;
    }

    logger.debug('PopupManager: Unregistered popup', {
      remainingTracked: this.openPopups.size,
    });
  }

  /**
   * Get debug information about current popup state
   * @returns Object with current state information
   */
  getDebugInfo(): {
    trackedCount: number;
    openMarkerId: number | string | null;
  } {
    return {
      trackedCount: this.openPopups.size,
      openMarkerId: this.openPopupMarkerId,
    };
  }
}

/**
 * Singleton instance for use in the map component
 */
export const popupManager = new PopupManager();
